import { useCallback } from 'react';
import { mapUserActionToStatus, mapReportActionToStatus } from '../utils/adminUtils';
import adminModerationService from '../services/adminModerationService';

export const useAdminActions = (setReports, setUsers, onDashboardRefresh) => {
  const handleReportAction = useCallback(async (reportId, action, note = '') => {
    try {
      // Map the action to the correct status before sending to service
      const mappedStatus = mapReportActionToStatus(action) || action;
      const response = await adminModerationService.updateReportStatus(reportId, mappedStatus, note);
      if (response.success) {
        setReports(prev => prev.map(report => 
          report.id === reportId 
            ? { 
                ...report, 
                status: mappedStatus,
                resolution: note || `Report ${action}d by admin`
              }
            : report
        ));
        // Trigger dashboard refresh after report action
        if (onDashboardRefresh) {
          onDashboardRefresh();
        }
      }
    } catch (error) {
      console.error('Error updating report:', error);
    }
  }, [setReports, onDashboardRefresh]);

  const handleUserAction = useCallback(async (userId, action, note = '', duration = null) => {
    try {
      let response;
      
      // Handle deletion separately from status updates
      if (action === 'delete') {
        response = await adminModerationService.deleteUser(userId, note, note);
        if (response.success) {
          // Remove user from state after successful deletion
          setUsers(prev => prev.filter(user => user.id !== userId));
          // Trigger dashboard refresh
          if (onDashboardRefresh) {
            onDashboardRefresh();
          }
        }
      } else {
        const dbStatus = mapUserActionToStatus(action);
        // Pass duration for suspension actions
        response = await adminModerationService.updateUserStatus(userId, dbStatus, note, duration);
        if (response.success) {
          setUsers(prev => prev.map(user => 
            user.id === userId 
              ? { 
                  ...user, 
                  moderation_status: {
                    ...user.moderation_status,
                    status: dbStatus
                  }
                }
              : user
          ));
          // Trigger dashboard refresh after user action (especially important for suspend/activate)
          if (onDashboardRefresh) {
            onDashboardRefresh();
          }
        }
      }
      
      return response;
    } catch (error) {
      console.error('Error updating user:', error);
      return { success: false, error: error.message };
    }
  }, [setUsers, onDashboardRefresh]);

  return {
    handleReportAction,
    handleUserAction
  };
};
